import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BrushIcon from "@mui/icons-material/Brush";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import AnimationIcon from "@mui/icons-material/MovieFilter";
import LayersIcon from "@mui/icons-material/Layers";
import WebIcon from "@mui/icons-material/Web";
import DevicesIcon from "@mui/icons-material/Devices";
import InsightsIcon from "@mui/icons-material/Insights";
import { useNavigate } from "react-router-dom";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { motion } from "framer-motion";

// ✅ Graphic Design Content Data
const graphicContent = [
  {
    section: "Basic Concepts",
    icon: <ColorLensIcon />,
    items: [
      {
        name: "Principles of Design",
        description: "Balance, contrast, hierarchy, alignment.",
        fullContent: [
          {
            heading: "Key Principles",
            points: [
              "Balance (symmetry/asymmetry)",
              "Contrast (light vs dark)",
              "Hierarchy (what’s most important)",
              "Alignment & spacing",
            ],
          },
        ],
      },
      {
        name: "Color Theory",
        description: "Using colors effectively in design.",
        fullContent: [
          {
            heading: "Example CSS Gradient",
            code: `h1 {
  background: linear-gradient(to right, #ff512f, #dd2476);
  -webkit-background-clip: text;
  color: transparent;
}`,
            language: "css",
          },
        ],
      },
    ],
  },
  {
    section: "Image Editing (Beginner Tools)",
    icon: <CameraAltIcon />,
    items: [
      {
        name: "Canva",
        description: "Quick designs for beginners.",
        fullContent: [
          {
            heading: "Overview",
            text: "Canva is drag-and-drop design for posters, social posts, and resumes.",
          },
        ],
      },
      {
        name: "GIMP",
        description: "Free Photoshop alternative.",
        fullContent: [
          {
            heading: "Key Features",
            points: ["Layer editing", "Filters", "Photo retouching"],
          },
        ],
      },
    ],
  },
  {
    section: "Intermediate Tools",
    icon: <BrushIcon />,
    items: [
      {
        name: "Adobe Illustrator",
        description: "Professional vector illustration.",
        fullContent: [
          {
            heading: "Use Cases",
            points: ["Logo design", "Iconography", "Print media"],
          },
          {
            heading: "Example Code (SVG)",
            code: `<svg height="100" width="100">
  <circle cx="50" cy="50" r="40" fill="orange" />
</svg>`,
            language: "xml",
          },
        ],
      },
      {
        name: "CorelDRAW",
        description: "Branding & packaging design.",
        fullContent: [
          {
            heading: "Overview",
            text: "Widely used in product packaging & signboard design.",
          },
        ],
      },
    ],
  },
  {
    section: "UI/UX Design",
    icon: <DevicesIcon />,
    items: [
      {
        name: "Figma",
        description: "Collaborative interface design.",
        fullContent: [
          {
            heading: "Example Code (CSS Button)",
            code: `button {
  background: linear-gradient(45deg, #ff5722, #ff9800);
  border-radius: 12px;
  color: white;
  padding: 12px 24px;
}`,
            language: "css",
          },
        ],
      },
      {
        name: "Adobe XD",
        description: "Wireframes & prototypes.",
        fullContent: [
          {
            heading: "Overview",
            text: "XD helps with clickable mockups and app design.",
          },
        ],
      },
    ],
  },
  {
    section: "Advanced Motion & 3D",
    icon: <AnimationIcon />,
    items: [
      {
        name: "After Effects",
        description: "Motion graphics & video effects.",
        fullContent: [
          {
            heading: "Key Uses",
            points: ["Cinematic titles", "Green screen removal", "VFX"],
          },
        ],
      },
      {
        name: "Blender",
        description: "3D design & animation.",
        fullContent: [
          {
            heading: "Example Blender Script",
            code: `import bpy
bpy.ops.mesh.primitive_cube_add(location=(0,0,0))`,
            language: "python",
          },
        ],
      },
    ],
  },
  {
    section: "Professional Branding",
    icon: <LayersIcon />,
    items: [
      {
        name: "Typography",
        description: "Fonts & hierarchy.",
        fullContent: [
          {
            heading: "Rules",
            points: ["Readable fonts", "Consistency", "Font pairing"],
          },
        ],
      },
      {
        name: "Logo Systems",
        description: "Design scalable branding.",
        fullContent: [
          {
            heading: "Principles",
            points: ["Simplicity", "Versatility", "Timeless design"],
          },
        ],
      },
    ],
  },
  {
    section: "Web & Digital Graphics",
    icon: <WebIcon />,
    items: [
      {
        name: "CSS Art",
        description: "Frontend graphics.",
        fullContent: [
          {
            heading: "Example Code (CSS Shapes)",
            code: `.circle {
  width: 100px;
  height: 100px;
  background: coral;
  border-radius: 50%;
}`,
            language: "css",
          },
        ],
      },
      {
        name: "SVG Animations",
        description: "Lightweight vector graphics.",
        fullContent: [
          {
            heading: "Example SVG Animation",
            code: `<circle cx="50" cy="50" r="40">
  <animate attributeName="r" from="10" to="40" dur="1s" repeatCount="indefinite"/>
</circle>`,
            language: "xml",
          },
        ],
      },
    ],
  },
  {
    section: "Future & Experimental",
    icon: <InsightsIcon />,
    items: [
      {
        name: "AI in Design",
        description: "Tools like MidJourney, DALL·E.",
        fullContent: [
          {
            heading: "Overview",
            text: "AI is now used to generate concept art, social posts, and idea variations instantly.",
          },
        ],
      },
      {
        name: "AR/VR Experiences",
        description: "Immersive 3D environments.",
        fullContent: [
          {
            heading: "Example Unity C#",
            code: `void Update() {
  transform.Rotate(0, 50 * Time.deltaTime, 0);
}`,
            language: "csharp",
          },
        ],
      },
    ],
  },
];

function GraphicDesigning() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ bgcolor: "#f4f6f8", minHeight: "100vh", pt: 12, pb: 8 }}>
      <Container maxWidth="xl">
        {/* Header Navigation */}
        <Box mb={4} display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              fontWeight: 700,
              color: "#034522",
              mr: 2,
              textTransform: "none",
              borderRadius: "50px",
              px: 3,
              "&:hover": { bgcolor: "rgba(3, 69, 34, 0.1)" },
            }}
          >
            Back to Services
          </Button>
          <Typography variant="h4" fontWeight={800} color="#1a1a1a" sx={{ fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
            Graphic Designing
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Sidebar Tabs (Desktop) / Top Tabs (Mobile) */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                background: "#fff",
              }}
            >
              <Tabs
                orientation={isMobile ? "horizontal" : "vertical"}
                variant="scrollable"
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  borderRight: { md: 1, xs: 0 },
                  borderColor: "divider",
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1A9E7F",
                    width: "4px",
                    left: { md: 0 },
                    right: "auto"
                  },
                }}
              >
                {graphicContent.map((section, index) => (
                  <Tab
                    key={index}
                    icon={<Box sx={{ color: activeTab === index ? "#1A9E7F" : "inherit" }}>{section.icon}</Box>}
                    iconPosition="start"
                    label={section.section}
                    sx={{
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontWeight: 600,
                      color: activeTab === index ? "#034522" : "#666",
                      fontSize: "1rem",
                      py: 3,
                      px: 3,
                      minHeight: 64,
                      transition: "all 0.3s",
                      "&.Mui-selected": {
                        color: "#034522",
                        bgcolor: "rgba(26, 158, 127, 0.08)",
                      },
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.02)",
                      }
                    }}
                  />
                ))}
              </Tabs>
            </Paper>
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={9}>
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  minHeight: "60vh",
                  bgcolor: "#fff"
                }}
              >
                <Box mb={4} display="flex" alignItems="center">
                    <Box 
                        sx={{ 
                            p: 1.5, 
                            borderRadius: '12px', 
                            bgcolor: 'rgba(3, 69, 34, 0.1)', 
                            color: '#034522',
                            mr: 2,
                            display: 'flex',
                            boxShadow: '0 4px 12px rgba(3, 69, 34, 0.1)'
                        }}
                    >
                        {graphicContent[activeTab].icon}
                    </Box>
                    <Typography variant="h5" fontWeight={800} color="#034522">
                    {graphicContent[activeTab].section}
                    </Typography>
                </Box>
                
                <Box>
                  {graphicContent[activeTab].items.map((item, i) => (
                    <Accordion
                      key={i}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: 2,
                        borderRadius: "16px !important",
                        border: "1px solid #edf2f7",
                        "&:before": { display: "none" },
                        "&:hover": { bgcolor: "#f8f9fa", borderColor: "#e2e8f0" },
                        transition: "all 0.2s"
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: "#1A9E7F" }} />}
                        sx={{ px: 3, py: 1 }}
                      >
                        <Box>
                          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', color: '#1a1a1a' }}>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="#666" sx={{ mt: 0.5 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pb: 4, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                        {item.fullContent.map((content, idx) => (
                          <Box key={idx} mt={3}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              gutterBottom
                              color="#1A9E7F"
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1A9E7F' }} />
                                {content.heading}
                            </Typography>
                            {content.text && (
                              <Typography paragraph variant="body2" color="#4a5568" mb={2} sx={{ lineHeight: 1.7 }}>
                                {content.text}
                              </Typography>
                            )}
                            {content.points && (
                                <ul style={{ marginLeft: "1.5rem", marginTop: 0, marginBottom: '16px', color: '#4a5568' }}>
                                    {content.points.map((point, pIdx) => (
                                        <li key={pIdx}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{point}</Typography>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {content.code && (
                              <Box
                                sx={{
                                  borderRadius: 3,
                                  overflow: "hidden",
                                  border: "1px solid #e2e8f0",
                                  marginTop: 1,
                                  boxShadow: "0 4px 6px rgba(0,0,0,0.02)"
                                }}
                              >
                                <SyntaxHighlighter
                                  language={content.language || "javascript"}
                                  style={atomDark}
                                  customStyle={{ margin: 0, padding: "20px", fontSize: "0.9rem", lineHeight: 1.5 }}
                                >
                                  {content.code}
                                </SyntaxHighlighter>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default GraphicDesigning;
